import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useOtpLog } from "../hooks/use-otp-logs";
import { format } from "date-fns";
import { OtpEventType, OtpStatus, OtpChannel } from "@/pages/otp-log/types/otp-log";
import { Skeleton } from "@/components/ui/skeleton";

interface OtpLogDetailModalProps {
  logId: string;
  open: boolean;
  onClose: () => void;
}

const getEventTypeBadgeVariant = (eventType: OtpEventType) => {
  switch (eventType) {
    case OtpEventType.OTP_VERIFIED:
      return "default";
    case OtpEventType.OTP_DELIVERED:
      return "secondary";
    case OtpEventType.OTP_SENT:
      return "outline";
    case OtpEventType.OTP_FAILED:
      return "destructive";
    case OtpEventType.OTP_EXPIRED:
      return "destructive";
    default:
      return "outline";
  }
};

const getStatusBadgeVariant = (status: OtpStatus) => {
  switch (status) {
    case OtpStatus.SUCCESS:
      return "default";
    case OtpStatus.FAILED:
      return "destructive";
    case OtpStatus.PENDING:
      return "secondary";
    default:
      return "outline";
  }
};

export function OtpLogDetailModal({
  logId,
  open,
  onClose,
}: OtpLogDetailModalProps) {
  const { data: log, isLoading, error } = useOtpLog(logId);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>OTP Log Details</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-600">
            Failed to load OTP log details
          </div>
        ) : !log ? (
          <div className="text-center py-8 text-muted-foreground">
            OTP log not found
          </div>
        ) : (
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Event Type
                </label>
                <div className="mt-1">
                  <Badge variant={getEventTypeBadgeVariant(log.event_type)}>
                    {log.event_type.replace("OTP_", "")}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Status
                </label>
                <div className="mt-1">
                  <Badge variant={getStatusBadgeVariant(log.status)}>
                    {log.status}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Channel
                </label>
                <div className="mt-1">
                  {log.channel ? (
                    <Badge variant="outline">{log.channel}</Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </div>
              </div>
            </div>

            {/* User Information */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold mb-3">User Information</h3>
              <div className="grid grid-cols-2 gap-4">
                {log.user && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Full Name
                    </label>
                    <div className="mt-1 text-sm">{log.user.full_name}</div>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Email
                  </label>
                  <div className="mt-1 text-sm">
                    {log.otpless_identifier && log.channel === OtpChannel.EMAIL ? (
                      log.otpless_identifier
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Phone
                  </label>
                  <div className="mt-1 text-sm">
                    {log.otpless_identifier && 
                     (log.channel === OtpChannel.SMS || log.channel === OtpChannel.WHATSAPP) ? (
                      log.otpless_identifier
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    OTP ID
                  </label>
                  <div className="mt-1 text-sm font-mono text-xs">
                    {log.id || (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold mb-3">Performance Metrics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Delivery Time
                  </label>
                  <div className="mt-1 text-sm">
                    {log.delivery_time_ms
                      ? `${(log.delivery_time_ms / 1000).toFixed(2)}s`
                      : <span className="text-muted-foreground">-</span>}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Verification Time
                  </label>
                  <div className="mt-1 text-sm">
                    {log.verification_time_ms
                      ? `${(log.verification_time_ms / 1000).toFixed(2)}s`
                      : <span className="text-muted-foreground">-</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {log.error_message && (
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold mb-3">Error Information</h3>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Error Message
                  </label>
                  <div className="mt-1 text-sm text-red-600 break-all">
                    {log.error_message}
                  </div>
                </div>
              </div>
            )}

            {/* Metadata */}
            {log.metadata && Object.keys(log.metadata).length > 0 && (
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold mb-3">Metadata</h3>
                <div className="bg-muted p-3 rounded-md">
                  <pre className="text-xs overflow-auto">
                    {JSON.stringify(log.metadata, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold mb-3">Timestamps</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Created At
                  </label>
                  <div className="mt-1 text-sm">
                    {format(new Date(log.created_at), "MMM dd, yyyy HH:mm:ss")}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Updated At
                  </label>
                  <div className="mt-1 text-sm">
                    {format(new Date(log.updated_at), "MMM dd, yyyy HH:mm:ss")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

